/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  let ghostGuardTimer = null;

  function isQuickBuyControl(element) {
    return element?.matches?.('quick-buy-drawer, quick-buy-popover');
  }

  function getQuickBuyHref(control) {
    return control?.getAttribute('href') || control?.dataset.quickBuyHref || control?.getAttribute('data-quick-buy-href') || '';
  }

  function rememberQuickBuyHref(control) {
    const href = getQuickBuyHref(control);
    if (href) {
      control.dataset.quickBuyHref = href;
    }
  }

  function restoreQuickBuyHref(control) {
    if (!control?.dataset.quickBuyHref) {
      return;
    }

    if (!control.hasAttribute('href')) {
      control.setAttribute('href', control.dataset.quickBuyHref);
    }
  }

  function escapeControlId(controlId) {
    return typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(controlId) : controlId;
  }

  function syncQuickBuyToggles(controlId, expanded) {
    document.querySelectorAll(`[aria-controls="${escapeControlId(controlId)}"]`).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (!expanded) {
        toggle.removeAttribute('aria-busy');
      }
    });
  }

  function installQuickBuyReloadFix() {
    ['quick-buy-drawer', 'quick-buy-popover'].forEach((tagName) => {
      const Constructor = customElements.get(tagName);
      if (!Constructor || Constructor.prototype.__yiannaReloadFixed) {
        return;
      }

      Constructor.prototype.__yiannaReloadFixed = true;

      let loadGeneration = 0;

      const originalLoad = Constructor.prototype._load;
      Constructor.prototype._load = async function patchedQuickBuyLoad() {
        rememberQuickBuyHref(this);
        restoreQuickBuyHref(this);
        return originalLoad.call(this);
      };

      Object.defineProperty(Constructor.prototype, 'open', {
        configurable: true,
        get() {
          return this.hasAttribute('open');
        },
        set(value) {
          if (value) {
            const generation = ++loadGeneration;
            rememberQuickBuyHref(this);
            restoreQuickBuyHref(this);

            (async () => {
              await this._load();

              if (generation !== loadGeneration) {
                return;
              }

              this.clientWidth;
              this.setAttribute('open', '');
            })();
            return;
          }

          loadGeneration += 1;
          this.removeAttribute('open');
        }
      });
    });
  }

  function armGhostClickGuard() {
    document.body.classList.add('quick-buy-ghost-guard');
    clearTimeout(ghostGuardTimer);
    ghostGuardTimer = setTimeout(() => {
      document.body.classList.remove('quick-buy-ghost-guard');
    }, 350);
  }

  installQuickBuyReloadFix();

  document.addEventListener('mousedown', (event) => {
    if (event.target.closest('quick-buy-drawer [data-action="close"], quick-buy-popover [data-action="close"], quick-buy-drawer .drawer__overlay, quick-buy-popover .popover__overlay')) {
      armGhostClickGuard();
    }
  }, true);

  document.addEventListener('openable-element:close', (event) => {
    if (!isQuickBuyControl(event.target)) {
      return;
    }

    const control = event.target;
    rememberQuickBuyHref(control);
    restoreQuickBuyHref(control);
    syncQuickBuyToggles(control.id, false);

    control.addEventListener('transitionend', (transitionEvent) => {
      if (transitionEvent.target !== control) {
        return;
      }

      restoreQuickBuyHref(control);
    }, { once: true });
  }, true);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[is="toggle-button"][aria-controls]');
    if (!toggle) {
      return;
    }

    const control = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!isQuickBuyControl(control)) {
      return;
    }

    rememberQuickBuyHref(control);
    restoreQuickBuyHref(control);

    if (toggle.getAttribute('aria-expanded') === 'true' && !control.hasAttribute('open')) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.removeAttribute('aria-busy');
    }
  }, true);

  function isDrawerQuickBuy(element) {
    return Boolean(element?.id?.startsWith('drawer-qb-'));
  }

  function mountDrawerQuickBuy(element) {
    if (!element || !isDrawerQuickBuy(element) || element.dataset.drawerQuickBuyMounted === 'true') {
      return;
    }

    element.dataset.drawerQuickBuyMounted = 'true';
    rememberQuickBuyHref(element);

    const existing = document.getElementById(element.id);
    if (existing && existing !== element) {
      existing.remove();
    }

    if (element.parentElement !== document.body) {
      document.body.appendChild(element);
    }
  }

  function observeQuickBuyElement(element) {
    if (!element || element.dataset.quickBuyObserved === 'true') {
      return;
    }

    element.dataset.quickBuyObserved = 'true';
    rememberQuickBuyHref(element);

    if (isDrawerQuickBuy(element)) {
      mountDrawerQuickBuy(element);
    }
  }

  function observeDrawerQuickBuy(drawerId) {
    const drawer = document.getElementById(drawerId);

    if (!drawer) {
      return;
    }

    drawer.querySelectorAll('quick-buy-drawer, quick-buy-popover').forEach(observeQuickBuyElement);

    new MutationObserver(() => {
      drawer.querySelectorAll('quick-buy-drawer, quick-buy-popover').forEach(observeQuickBuyElement);
    }).observe(drawer, {
      childList: true,
      subtree: true
    });
  }

  function initQuickBuyHrefs() {
    document.querySelectorAll('quick-buy-drawer, quick-buy-popover').forEach(observeQuickBuyElement);
  }

  function resetMiniCartScroll() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart) {
      return;
    }

    miniCart.querySelectorAll('.mini-cart__scroll, .drawer__content').forEach((scrollNode) => {
      scrollNode.scrollTop = 0;
    });
  }

  let cartChatObserver = null;

  function hideChatWidgets(isOpen) {
    const chatSelectors = [
      'shopify-chat',
      '#shopify-chat',
      '#ShopifyChat',
      'inbox-online-store-chat',
      '#launcher',
      '#hubspot-messages-iframe-container',
      '#tidio-chat',
      '#gorgias-chat-container',
      '.intercom-lightweight-app-launcher',
      '[class*="shopify-chat"]',
      'iframe[title*="chat" i]',
      'iframe[id*="chat" i]'
    ];

    chatSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        node.style.setProperty('display', isOpen ? 'none' : '', 'important');
        node.style.setProperty('visibility', isOpen ? 'hidden' : '', 'important');
        node.style.setProperty('pointer-events', isOpen ? 'none' : '', 'important');
        node.style.setProperty('z-index', isOpen ? '0' : '', 'important');
      });
    });
  }

  function syncCartOpenState() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart) {
      return;
    }

    const isOpen = miniCart.hasAttribute('open');
    document.documentElement.classList.toggle('yianna-cart-open', isOpen);
    hideChatWidgets(isOpen);

    if (isOpen && !cartChatObserver) {
      cartChatObserver = new MutationObserver(() => hideChatWidgets(true));
      cartChatObserver.observe(document.body, { childList: true, subtree: true });
    } else if (!isOpen && cartChatObserver) {
      cartChatObserver.disconnect();
      cartChatObserver = null;
    }
  }

  function initMiniCartScrollReset() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart) {
      return;
    }

    syncCartOpenState();

    const observer = new MutationObserver(() => {
      syncCartOpenState();

      if (miniCart.hasAttribute('open')) {
        requestAnimationFrame(resetMiniCartScroll);
      }
    });

    observer.observe(miniCart, {
      attributes: true,
      attributeFilter: ['open']
    });

    document.addEventListener('cart:updated', resetMiniCartScroll);
    document.addEventListener('cart:refresh', resetMiniCartScroll);
  }

  let shouldOpenMiniCartFromQuickBuy = false;
  let shouldOpenMiniCartFromProductPage = false;

  function isMainProductPageForm(form) {
    return Boolean(
      form?.matches?.('form[is="product-form"]')
        && form.id?.startsWith?.('product-form-main-')
        && !form.closest('quick-buy-drawer, quick-buy-popover, .drawer')
    );
  }

  function initProductPageCartDrawer() {
    document.addEventListener('variant:added', (event) => {
      if (!isMainProductPageForm(event.target)) {
        return;
      }

      shouldOpenMiniCartFromProductPage = true;
    });

    document.documentElement.addEventListener('cart:refresh', (event) => {
      if (!shouldOpenMiniCartFromProductPage) {
        return;
      }

      shouldOpenMiniCartFromProductPage = false;
      if (!event.detail) {
        return;
      }

      event.detail.openMiniCart = true;
    }, true);

    document.addEventListener('cart-notification:show', (event) => {
      if (event.detail?.status === 'error' || !isMainProductPageForm(event.target)) {
        return;
      }

      event.stopImmediatePropagation();
    }, true);
  }

  function initQuickBuyCartDrawer() {
    if (window.themeVariables?.settings?.cartType !== 'drawer') {
      return;
    }

    document.addEventListener('variant:added', (event) => {
      const quickBuyDrawer = event.target?.closest?.('quick-buy-drawer');
      if (!quickBuyDrawer) {
        return;
      }

      shouldOpenMiniCartFromQuickBuy = true;
      quickBuyDrawer.open = false;
    });

    document.documentElement.addEventListener('cart:refresh', (event) => {
      if (!shouldOpenMiniCartFromQuickBuy) {
        return;
      }

      shouldOpenMiniCartFromQuickBuy = false;
      if (!event.detail) {
        return;
      }

      event.detail.openMiniCart = true;
    }, true);

    document.addEventListener('cart-notification:show', (event) => {
      if (!event.target?.closest?.('quick-buy-drawer') || event.detail?.status === 'error') {
        return;
      }

      event.stopImmediatePropagation();
    }, true);
  }

  function initFpsShowcaseLockGuard() {
    function resetFpsShowcaseImages() {
      document.querySelectorAll('.shopify-section--featured-product-showcase .fps-card__image').forEach((img) => {
        img.classList.remove('is-entering');
        if (document.documentElement.classList.contains('lock-all')) {
          img.style.setProperty('opacity', '1', 'important');
          img.style.setProperty('transform', 'none', 'important');
        } else {
          img.style.removeProperty('opacity');
          img.style.removeProperty('transform');
        }
      });
      document.querySelectorAll('.shopify-section--featured-product-showcase .fps-card__image-clone').forEach((clone) => {
        clone.remove();
      });
    }

    new MutationObserver(resetFpsShowcaseImages).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    document.addEventListener('openable-element:open', (event) => {
      if (event.target?.id === 'mini-cart') {
        requestAnimationFrame(resetFpsShowcaseImages);
      }
    });
  }

  function initQuickBuySupport() {
    initQuickBuyHrefs();
    initMiniCartScrollReset();
    initProductPageCartDrawer();
    initQuickBuyCartDrawer();
    initFpsShowcaseLockGuard();
    observeDrawerQuickBuy('mini-cart');
    observeDrawerQuickBuy('search-drawer');

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) {
            return;
          }

          if (node.matches('quick-buy-drawer, quick-buy-popover')) {
            observeQuickBuyElement(node);
          }

          node.querySelectorAll?.('quick-buy-drawer, quick-buy-popover').forEach(observeQuickBuyElement);
        });
      });
    }).observe(document.body, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickBuySupport);
  } else {
    initQuickBuySupport();
  }
})();
